/**
 * Your business Overview (`/business`, TMMRSL-0058): the business identity (name, slug, status,
 * currency), a facts card from the `ResellerTenantView`, the Rate card panel, then this tenant's
 * Dashboard. A tenant that cannot sell (`sellable: false`) shows why above the card, from the
 * server's `sellableReason` (TMMAPI-0095, 0097); no agreement read is made.
 */
import { Alert, Box, Chip, Grid, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { ResellerTenantView } from '@/api/resellerAdmin';
import { notSellableText } from '@/business/notSellable';
import { PageHeader } from '@/components/PageHeader';
import { DashboardScreen } from '@/features/dashboard/DashboardScreen';
import { formatCount, formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { NotPricedState } from './NotPricedState';
import { RateCardPanel } from './RateCardPanel';
import { TenantStatusChip } from './TenantStatusChip';
import { useTenant } from './useTenant';

/**
 * Why a `sellable: false` tenant cannot sell, and what to do about it (NOTIF-002). No agreement, or
 * no reason sent, → the not-priced notice.
 */
function NotSellableNotice({ tenant }: { tenant: ResellerTenantView }) {
  const { t } = useTranslation();
  const text = notSellableText(tenant.sellableReason, tenant.currency, t);
  return text ? (
    <Alert severity={tenant.sellableReason === 'NO_RATE_CARD' ? 'info' : 'warning'}>{text}</Alert>
  ) : (
    <NotPricedState />
  );
}

export function TenantOverview() {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const tenant = useTenant();
  let createdLabel: string;
  try {
    createdLabel = formatInstant(
      tenant.createdAt,
      locale,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      { dateStyle: 'medium' },
    );
  } catch {
    createdLabel = t('tenant.card.none');
  }
  const facts = [
    {
      key: 'subscribers',
      label: t('tenant.card.activeSubscribers'),
      value:
        tenant.activeSubscribers == null
          ? t('statements.unavailable')
          : formatCount(tenant.activeSubscribers, locale),
    },
    {
      key: 'addons',
      label: t('tenant.card.addons'),
      value:
        tenant.addonsActive.length > 0 ? tenant.addonsActive.join(', ') : t('tenant.card.none'),
    },
    {
      key: 'ai',
      label: t('tenant.card.aiDefault'),
      value: tenant.aiEnabledDefault ? t('tenant.card.aiOn') : t('tenant.card.aiOff'),
    },
    {
      key: 'created',
      label: t('tenant.card.created'),
      value: createdLabel,
    },
  ];

  return (
    <Box>
      <PageHeader
        id="tenantOverview"
        title={tenant.name || tenant.slug}
        actions={
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant="mono" sx={{ color: 'text.secondary' }}>
              {tenant.slug}
            </Typography>
            <TenantStatusChip status={tenant.status} />
            {tenant.currency ? (
              <Chip
                size="small"
                variant="outlined"
                label={t('tenant.currencyChip', { currency: tenant.currency })}
              />
            ) : null}
          </Stack>
        }
      />
      {tenant.sellable === false ? (
        <Box sx={{ mb: 2.5 }}>
          <NotSellableNotice tenant={tenant} />
        </Box>
      ) : null}
      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Grid container spacing={2}>
          {facts.map((fact) => (
            <Grid key={fact.key} size={{ xs: 6, md: 3 }}>
              <Typography variant="overline" component="p" sx={{ color: tokens.text2 }}>
                {fact.label}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                {fact.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <RateCardPanel />
      <DashboardScreen />
    </Box>
  );
}
